import type { Role } from '@copilot/shared';
import {
  BookOpen,
  Building2,
  CalendarCheck,
  CalendarClock,
  Coins,
  LayoutDashboard,
  ScrollText,
  Settings,
  UserCircle,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Which roles may open each area of the admin panel. The server enforces the
 * real permissions; this only hides navigation the role can't use.
 */
export type Area =
  | 'dashboard'
  | 'meetings'
  | 'myMeetings'
  | 'clients'
  | 'users'
  | 'usage'
  | 'kb'
  | 'settings'
  | 'audit'
  | 'account';

const AREA_ROLES: Record<Area, readonly Role[]> = {
  dashboard: ['admin', 'manager'],
  meetings: ['admin', 'manager'],
  myMeetings: ['admin', 'manager', 'rep'],
  clients: ['admin', 'manager'],
  users: ['admin', 'manager'],
  usage: ['admin', 'manager'],
  kb: ['admin'],
  settings: ['admin'],
  audit: ['admin'],
  account: ['admin', 'manager', 'rep'],
};

export function canAccess(role: Role, area: Area): boolean {
  return AREA_ROLES[area].includes(role);
}

export interface NavItem {
  area: Area;
  to: string;
  label: string;
  icon: LucideIcon;
}

export const NAV_ITEMS: NavItem[] = [
  { area: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { area: 'meetings', to: '/meetings', label: 'Meetings', icon: CalendarClock },
  { area: 'myMeetings', to: '/my-meetings', label: 'My meetings', icon: CalendarCheck },
  { area: 'clients', to: '/clients', label: 'Clients', icon: Building2 },
  { area: 'users', to: '/users', label: 'Users & teams', icon: Users },
  { area: 'usage', to: '/usage', label: 'Usage & costs', icon: Coins },
  { area: 'kb', to: '/kb', label: 'Knowledge base', icon: BookOpen },
  { area: 'settings', to: '/settings', label: 'Settings', icon: Settings },
  { area: 'audit', to: '/audit', label: 'Audit log', icon: ScrollText },
  { area: 'account', to: '/account', label: 'My account', icon: UserCircle },
];

export function navItemsFor(role: Role): NavItem[] {
  return NAV_ITEMS.filter((item) => canAccess(role, item.area));
}

/** Landing page after sign-in. */
export function homePathFor(role: Role): string {
  return role === 'rep' ? '/my-meetings' : '/dashboard';
}

export const ROLE_LABELS: Record<Role, string> = { admin: 'Admin', manager: 'Manager', rep: 'Rep' };
